import { newId, slugify, uniqueSlug } from '../ids.js';
import { documentContentSchema } from './schema.js';
import { CONTENT_SCHEMA_VERSION, type DocumentContent, type RichDoc, type RichNode, type Section } from './types.js';

/**
 * Keys that must never survive into a stored attribute bag.
 *
 * Content arrives as JSON from the browser. `JSON.parse` keeps `__proto__` as
 * an own property rather than assigning it, but that object then flows through
 * spreads and merges in the renderers, so it is removed at the boundary.
 */
const FORBIDDEN_ATTR_KEYS = new Set(['__proto__', 'constructor', 'prototype']);

function sanitiseAttrs(value: unknown, depth = 0): unknown {
  if (Array.isArray(value)) return depth > 6 ? [] : value.map((item) => sanitiseAttrs(item, depth + 1));
  if (value === null || typeof value !== 'object') return value;
  if (depth > 6) return {};
  const out: Record<string, unknown> = {};
  for (const [key, entry] of Object.entries(value as Record<string, unknown>)) {
    if (FORBIDDEN_ATTR_KEYS.has(key)) continue;
    out[key] = sanitiseAttrs(entry, depth + 1);
  }
  return out;
}

/** Recursively cleans a rich-text node tree's attributes. */
function sanitiseNode(node: RichNode): RichNode {
  const cleaned: RichNode = { ...node };
  if (cleaned.attrs) cleaned.attrs = sanitiseAttrs(cleaned.attrs) as Record<string, unknown>;
  if (cleaned.marks) {
    cleaned.marks = cleaned.marks.map((mark) => ({
      ...mark,
      ...(mark.attrs ? { attrs: sanitiseAttrs(mark.attrs) as Record<string, unknown> } : {}),
    }));
  }
  if (cleaned.content) cleaned.content = cleaned.content.map(sanitiseNode);
  return cleaned;
}

export function emptyBody(): RichDoc {
  return { type: 'doc', content: [{ type: 'paragraph' }] };
}

export function emptyContent(): DocumentContent {
  return { schemaVersion: CONTENT_SCHEMA_VERSION, sections: [] };
}

export function makeSection(partial: Partial<Section> = {}): Section {
  const title = partial.title ?? 'Untitled section';
  return {
    id: partial.id ?? newId('sec'),
    title,
    anchor: partial.anchor ?? slugify(title),
    numbered: partial.numbered ?? true,
    guidance: partial.guidance,
    locked: partial.locked,
    internal: partial.internal,
    body: partial.body ?? emptyBody(),
    children: partial.children ?? [],
  };
}

/**
 * Validates and repairs untrusted content: fills in missing ids, guarantees
 * anchors are unique within the document, and drops empty bodies.
 *
 * Anchors are stable once assigned so that cross references survive renames.
 */
export function normalizeContent(input: unknown): DocumentContent {
  const parsed = documentContentSchema.parse(input ?? emptyContent());
  const takenAnchors = new Set<string>();
  const takenIds = new Set<string>();

  const walk = (sections: Section[], depth: number): Section[] =>
    sections.map((raw) => {
      const section = raw as Section;
      let id = section.id?.trim();
      if (!id || takenIds.has(id)) id = newId('sec');
      takenIds.add(id);

      const title = (section.title ?? '').trim();
      const desired = slugify(section.anchor?.trim() || title || 'section');
      const anchor = uniqueSlug(desired, takenAnchors);

      const body: RichDoc =
        section.body && section.body.type === 'doc'
          ? { type: 'doc', content: Array.isArray(section.body.content) ? section.body.content.map(sanitiseNode) : [] }
          : emptyBody();

      return {
        id,
        title,
        anchor,
        numbered: section.numbered !== false,
        ...(section.guidance ? { guidance: section.guidance } : {}),
        ...(section.locked ? { locked: true } : {}),
        ...(section.internal ? { internal: true } : {}),
        body,
        // Depth is validated by the schema, so nothing is dropped here.
        children: walk(section.children ?? [], depth + 1),
      } satisfies Section;
    });

  return { schemaVersion: CONTENT_SCHEMA_VERSION, sections: walk(parsed.sections as Section[], 1) };
}

/** Depth-first walk over every section in the tree. */
export function walkSections(
  content: DocumentContent,
  visit: (section: Section, depth: number, parents: Section[]) => void,
): void {
  const recurse = (sections: Section[], depth: number, parents: Section[]) => {
    for (const section of sections) {
      visit(section, depth, parents);
      recurse(section.children, depth + 1, [...parents, section]);
    }
  };
  recurse(content.sections, 1, []);
}

export function findSection(content: DocumentContent, predicate: (s: Section) => boolean): Section | null {
  let found: Section | null = null;
  walkSections(content, (section) => {
    if (!found && predicate(section)) found = section;
  });
  return found;
}

export function countSections(content: DocumentContent): number {
  let n = 0;
  walkSections(content, () => {
    n += 1;
  });
  return n;
}
